import { useRef, useMemo, useCallback, useEffect, useState } from "react";
import type { StaffDetail } from "@/api/guide";
import { AUTO_PLAY_INTERVAL } from "../constants";

interface BannerCarouselProps {
  guide: StaffDetail;
  onNavigateBack: () => void;
  onImageClick?: (index: number) => void;
}

export default function BannerCarousel({ guide, onNavigateBack, onImageClick }: BannerCarouselProps) {
  const bannerImages = useMemo(
    () => guide ? [guide.coverImage, ...guide.images.filter((img) => img !== guide.coverImage)] : [],
    [guide],
  );
  const bannerRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const scrollToSlide = useCallback((index: number) => {
    if (!bannerRef.current || !bannerImages.length) return;
    const total = index % bannerImages.length;
    setCurrentSlide(total);
    bannerRef.current.scrollLeft = total * bannerRef.current.clientWidth;
  }, [bannerImages]);

  // 自动播放
  useEffect(() => {
    if (bannerImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => {
        const next = (prev + 1) % bannerImages.length;
        scrollToSlide(next);
        return next;
      });
    }, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerImages.length]);

  const handleBannerScroll = useCallback(() => {
    if (!bannerRef.current) return;
    const scrollLeft = bannerRef.current.scrollLeft;
    const itemWidth = bannerRef.current.clientWidth;
    const idx = Math.round(scrollLeft / itemWidth);
    if (idx !== currentSlide && idx >= 0 && idx < bannerImages.length) setCurrentSlide(idx);
  }, [currentSlide, bannerImages.length]);

  return (
    <div className="relative" style={{ height: 320 }}>
      {/* 滑动容器 */}
      <div
        ref={bannerRef}
        onScroll={handleBannerScroll}
        className="flex overflow-x-auto snap-x snap-mandatory"
        style={{
          height: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          scrollSnapType: "x mandatory",
        }}
      >
        {bannerImages.map((imgUrl, i) => (
          <img
            key={`${imgUrl}-${i}`}
            src={imgUrl}
            alt={`${guide.name} ${i + 1}`}
            className="flex-shrink-0 w-full h-full object-cover snap-start"
            draggable={false}
            style={{ scrollSnapAlign: "start" }}
          />
        ))}
      </div>

      {/* 渐变遮罩 */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)" }}
      />

      {/* 返回按钮 */}
      <button
        onClick={onNavigateBack}
        className="absolute top-12 left-4 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer z-10"
        style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)" }}
      >
        <i className="ri-arrow-left-line text-white" style={{ fontSize: 20 }} />
      </button>

      {/* 轮播指示器 */}
      {bannerImages.length > 1 && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {bannerImages.slice(0, 9).map((_, i) => (
            <div
              key={i}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === currentSlide ? 16 : 6,
                height: 6,
                background: i === currentSlide ? "#FFF" : "rgba(255,255,255,0.45)",
              }}
            />
          ))}
        </div>
      )}

      {/* ====== 头像 + 姓名（叠加在大图底部） ====== */}
      <ProfileHeader guide={guide} />
    </div>
  );
}

/** 头像 + 姓名 + 性别年龄标签 */
function ProfileHeader({ guide }: { guide: StaffDetail }) {
  return (
    <div className="absolute -bottom-10 left-5 right-5 flex items-end gap-3">
      <div className="relative flex-shrink-0">
        <img
          src={guide.avatar}
          alt={guide.name}
          className="w-[72px] h-[72px] rounded-full object-cover border-[3px] border-white"
        />
        {(guide.age > 0 || guide.sex !== undefined) && (
          <span
            className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-xs font-medium text-white whitespace-nowrap"
            style={{ background: guide.sex === 2 ? "#F472B6" : "#60A5FA" }}
          >
            {guide.sex === 2 ? "♀" : guide.sex === 1 ? "♂" : ""}
            {guide.age > 0 ? ` ${guide.age}` : ""}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0 pt-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-bold text-white">{guide.name}</h1>
          {guide.badge && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white whitespace-nowrap" style={{ background: "#FF6B35" }}>
              {guide.badge}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-1">
          <i className="ri-map-pin-line" style={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }} />
          <span className="text-xs text-white" style={{ opacity: 0.8 }}>{guide.distance || guide.city || "未知位置"}</span>
        </div>
      </div>
    </div>
  );
}
